/**
 * player-search-parsers.ts - HTML Parsers for Player Search Results
 *
 * Parses HTML responses from FIDE and Swiss Chess databases
 * and extracts player information.
 */

/**
 * Parsed player result
 */
export interface PlayerSearchResult {
  firstName: string;
  lastName: string;
  fideId?: string;
  rating: number;
  ratingStd?: number;    // FIDE Standard/Classical rating
  ratingRapid?: number;  // FIDE Rapid rating
  ratingBlitz?: number;  // FIDE Blitz rating
  title?: string;
  federation?: string;
  club?: string;
  federationCode?: string;
}

/**
 * Parse FIDE search results from HTML
 * @param html - The HTML response from FIDE
 * @returns Array of parsed player objects
 */
export function parseFideResults(html: string): PlayerSearchResult[] {
  // Remove <base> tags to prevent CSP violations
  const cleanHtml = html.replace(/<base[^>]*>/gi, '');

  const parser = new DOMParser();
  const doc = parser.parseFromString(cleanHtml, 'text/html');
  const players: PlayerSearchResult[] = [];
  const seenIds = new Set<string>();

  // The search results are in tbody rows
  const rows = doc.querySelectorAll('tbody tr');

  for (const row of rows) {
    const cells = row.querySelectorAll('td');

    // Structure: FIDE ID | Name | Title | Tr.T. | Fed | Std. | Rpd. | Blz. | B-Year
    if (cells.length >= 9) {
      const fideId = cells[0]?.textContent?.trim();
      const nameCell = cells[1];
      const title = cells[2]?.textContent?.trim();
      const federation = cells[4]?.textContent?.trim();
      const stdRatingStr = cells[5]?.textContent?.trim();
      const rapidRatingStr = cells[6]?.textContent?.trim();
      const blitzRatingStr = cells[7]?.textContent?.trim();

      // Extract name from the link
      const link = nameCell?.querySelector('a');
      if (link && fideId) {
        const fullName = link.textContent?.trim() || '';

        // Parse all ratings
        const ratingStd = stdRatingStr ? parseInt(stdRatingStr) : undefined;
        const ratingRapid = rapidRatingStr ? parseInt(rapidRatingStr) : undefined;
        const ratingBlitz = blitzRatingStr ? parseInt(blitzRatingStr) : undefined;

        // Default rating is standard, fallback to rapid, then blitz
        let rating = 0;
        if (ratingStd && !isNaN(ratingStd)) {
          rating = ratingStd;
        } else if (ratingRapid && !isNaN(ratingRapid)) {
          rating = ratingRapid;
        } else if (ratingBlitz && !isNaN(ratingBlitz)) {
          rating = ratingBlitz;
        }

        // Split name - FIDE format is typically "Lastname, Firstname"
        let firstName = '';
        let lastName = '';
        if (fullName.includes(',')) {
          const parts = fullName.split(',').map(p => p.trim());
          lastName = parts[0] || '';
          firstName = parts[1] || '';
        } else {
          // Fallback: split on last space
          const parts = fullName.trim().split(' ');
          if (parts.length === 1) {
            firstName = parts[0] || '';
            lastName = '';
          } else {
            lastName = parts[parts.length - 1] || '';
            firstName = parts.slice(0, -1).join(' ');
          }
        }

        // Add player if valid and not duplicate
        if (fullName && !seenIds.has(fideId)) {
          seenIds.add(fideId);
          players.push({
            firstName,
            lastName,
            fideId,
            rating,
            ratingStd: ratingStd && !isNaN(ratingStd) ? ratingStd : undefined,
            ratingRapid: ratingRapid && !isNaN(ratingRapid) ? ratingRapid : undefined,
            ratingBlitz: ratingBlitz && !isNaN(ratingBlitz) ? ratingBlitz : undefined,
            title: title && title !== '' ? title : undefined,
            federation: federation ? federation.replace(/\s+/g, ' ').trim() : undefined // Clean up federation (remove extra spaces/SVG text)
          });
        }
      }
    }
  }

  return players.slice(0, 10); // Limit to 10 results
}

/**
 * Parse Swiss Chess search results from HTML
 * @param html - The HTML response from Swiss Chess
 * @returns Array of parsed player objects
 */
export function parseSwissChessResults(html: string): PlayerSearchResult[] {
  // Remove <base> tags to prevent CSP violations
  const cleanHtml = html.replace(/<base[^>]*>/gi, '');

  const parser = new DOMParser();
  const doc = parser.parseFromString(cleanHtml, 'text/html');
  const players: PlayerSearchResult[] = [];
  const seenIds = new Set<string>();

  // The search results are in table rows with class 'resulttable'
  // Note: We look for tr with valign="top" to skip the header row
  const rows = doc.querySelectorAll('table.resulttable tr[valign="top"]');

  for (const row of rows) {
    const cells = row.querySelectorAll('td');

    // Structure: Checkbox | Code | Fide-Code | Fide-Fed. | Name | Vorname | S | Kategorie | Club | Fide | 3/25 | 4/25 | # | Diff
    if (cells.length >= 14) {
      const federationCode = cells[1]?.textContent?.trim();
      const fideId = cells[2]?.textContent?.trim();
      const lastName = cells[4]?.textContent?.trim();
      const firstName = cells[5]?.textContent?.trim();
      const club = cells[8]?.textContent?.trim();
      const fideRating = cells[9]?.textContent?.trim();
      const federationRating = cells[10]?.textContent?.trim();

      // Use federation rating if available, otherwise FIDE rating
      let rating = 0;
      if (federationRating && federationRating !== '') {
        rating = parseInt(federationRating);
      } else if (fideRating && fideRating !== '') {
        rating = parseInt(fideRating);
      }

      // Add player if valid and not duplicate
      if ((firstName || lastName) && federationCode && !seenIds.has(federationCode)) {
        seenIds.add(federationCode);
        players.push({
          firstName: firstName || '',
          lastName: lastName || '',
          fideId: fideId || undefined,
          rating: isNaN(rating) ? 0 : rating,
          club: club && club !== '' ? club : undefined,
          federation: 'SUI',
          federationCode
        });
      }
    }
  }

  return players.slice(0, 200); // Limit to 200 results (club searches can return many)
}
